import { CloseOutlined, SwapOutlined } from '@ant-design/icons';
import { Button, Card, Drawer, Empty, Pagination, Spin, Timeline, Typography } from 'antd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import useAPIQuery from '../../api/useAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import PageTitle from '../../components/PageTitle';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { JobPostingId, VixtingAdmissionId } from '../../types';
import styles from './VixtingAdmissionLogs.module.css';

const colorMap: Record<string, string> = {
  ERROR: styles.errorColor,
  INFO: styles.infoColor,
  SUCCESS: styles.successColor,
};

const VixtingAdmissionLogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useTypedNavigate();
  const { tenant, id, vixting_admission_id } = useTypedParams({
    tenant: String,
    id: JobPostingId,
    vixting_admission_id: VixtingAdmissionId,
  });

  const { t } = useTranslation();

  const vixtingAdmission = useAPIQuery([`/vixting/vixting-admissions/${vixting_admission_id}/`]);

  const recruitmentProcess = useAPIQuery(
    [`/recruitment-processes/${vixtingAdmission?.data?.recruitment_process}/`],
    { enabled: !!vixtingAdmission?.data?.recruitment_process }
  );

  const vixtingLogs = useAPIQuery([
    `/vixting/vixting-admissions-logs/`,
    {
      params: {
        vixting_admission: vixting_admission_id,
        page: parseInt(searchParams.get('logPage') || '1', 10),
        page_size: parseInt(searchParams.get('logPageSize') || '10', 10),
      },
    },
  ]);

  const onClose = () => {
    searchParams.delete('logPage');
    searchParams.delete('logPageSize');

    navigate({
      pathname: '/:tenant/opportunities/:id/analysis/',
      params: { tenant, id },
      search: searchParams,
    });
  };

  return (
    <Drawer
      open
      size="large"
      className={styles.drawer}
      onClose={onClose}
      closable={false}
      title={
        <PageTitle
          title={`${t('Logs da Integração')} | ${recruitmentProcess.data?.candidate?.name || '-'}`}
          level={3}
          extra={[
            <Button key="close" type="text" onClick={onClose} shape="circle">
              <CloseOutlined />
            </Button>,
          ]}
        />
      }
    >
      <ErrorResult error={vixtingLogs.error || recruitmentProcess.error || vixtingAdmission.error}>
        <Spin
          spinning={
            vixtingLogs.isLoading || recruitmentProcess.isLoading || vixtingAdmission.isLoading
          }
        >
          {!vixtingLogs.data?.count ? (
            <Empty />
          ) : (
            <>
              <Timeline data-testid="vixting-logs-timeline">
                {(vixtingLogs.data?.results || []).map((vixtingLog, index) => (
                  <div key={index}>
                    <Timeline.Item
                      dot={<SwapOutlined className={colorMap[vixtingLog.type || 'INFO']} />}
                    >
                      <Card>
                        <Typography.Text type="secondary">
                          {moment(vixtingLog.created_at).format('LLL')}
                        </Typography.Text>
                        <br />
                        <Typography.Text>
                          {vixtingLog.status_code && (
                            <Typography.Text strong>
                              Status {vixtingLog.status_code} -{' '}
                            </Typography.Text>
                          )}
                          <Typography.Text>{vixtingLog.message || '-'}</Typography.Text>
                        </Typography.Text>
                        {vixtingLog.response_data && (
                          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {Object.entries(vixtingLog.response_data).map(([key, value]) => (
                              <>
                                <Typography.Text strong>{key}:</Typography.Text>
                                <Typography.Text>
                                  {' '}
                                  {typeof value === 'object'
                                    ? JSON.stringify(value, null, 2)
                                    : String(value)}
                                </Typography.Text>
                                <br />
                              </>
                            ))}
                          </pre>
                        )}
                      </Card>
                    </Timeline.Item>
                  </div>
                ))}
              </Timeline>
              <Pagination
                onChange={(page, pageSize) => {
                  searchParams.set('logPage', page.toString());
                  searchParams.set('logPageSize', pageSize.toString());
                  setSearchParams(searchParams);
                }}
                current={parseInt(searchParams.get('logPage') || '1', 10)}
                total={vixtingLogs.data?.count || 0}
                pageSize={parseInt(searchParams.get('logPageSize') || '10', 10)}
              />
            </>
          )}
        </Spin>
      </ErrorResult>
    </Drawer>
  );
};

export default withRouteChange()(VixtingAdmissionLogs);
